import { flatten, reverse } from 'lodash';
import { useMemo, useRef } from 'react';
import { useTheme } from 'styled-components';

import { BladeSide } from './BladeDamagePlotContainer';
import { getLabelParametersFromRangePath } from './pointUtils';

export type SelectionRangeProps = {
  distance: [number, number];
  chord?: [number, number];
  rawSelection: [[number, number], [number, number]];
  xScale: d3.ScaleLinear<number, number, never>;
  yScale: d3.ScaleLinear<number, number, never>;
  shapePoints: [number, number][];
  bladeLength: number;
  bladeSide: BladeSide;
  hideLabel?: boolean;
};

/**
 * Given the selection drawn by a D3 Brush, redraws the box to match the blade
 * shape and contour, and adds a label of the selected distance and chord ranges
 */

export const SelectionRange: React.FunctionComponent<SelectionRangeProps> = ({
  distance,
  chord,
  xScale,
  yScale,
  rawSelection,
  shapePoints,
  bladeLength,
  bladeSide,
  hideLabel = false,
}) => {
  const theme = useTheme();
  const boxRef = useRef<SVGPathElement>(null);
  const textRef = useRef<SVGTextElement>(null);
  const yRange = useMemo(() => yScale.range(), [yScale]);

  // Subset of points for the blade shape curve at the selected distance range
  const shapePointsSubset = shapePoints.slice(
    Math.round((100 * distance[0]) / bladeLength),
    Math.round((100 * distance[1]) / bladeLength) + 1
  );

  const path = boxRef.current?.attributes?.getNamedItem('d')?.nodeValue;
  const labelParameters = getLabelParametersFromRangePath(path);

  const textBoundingBox = textRef.current?.getBBox();
  const textBackgroundAttributes = useMemo(() => {
    if (textBoundingBox) {
      return {
        x: textBoundingBox.x - 4,
        y: textBoundingBox.y - 2,
        width: textBoundingBox.width + 8,
        height: textBoundingBox.height + 4,
      };
    }

    return {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
    };
  }, [textBoundingBox]);

  return (
    <>
      {bladeSide === BladeSide.PressureSide && chord && (
        <path
          className="selection-range"
          ref={boxRef}
          d={[
            ...flatten(
              shapePointsSubset.map((point, i) => [
                i === 0 ? 'M' : 'L',
                xScale(point[0]),
                yScale(point[1]) + ((100 - chord[0]) / 100) * (yRange[0] - yScale(point[1])),
              ])
            ),
            ...flatten(
              reverse(shapePointsSubset).map(point => [
                'L',
                xScale(point[0]),
                yScale(point[1]) + ((100 - chord[1]) / 100) * (yRange[0] - yScale(point[1])),
              ])
            ),
            'Z',
          ].join(' ')}
        />
      )}
      {bladeSide === BladeSide.SuctionSide && chord && (
        <path
          className="selection-range"
          ref={boxRef}
          d={[
            ...flatten(
              shapePointsSubset.map((point, i) => [
                i === 0 ? 'M' : 'L',
                xScale(point[0]),
                (chord[1] / 100) * yScale(point[1]),
              ])
            ),
            ...flatten(
              reverse(shapePointsSubset).map(point => [
                'L',
                xScale(point[0]),
                (chord[0] / 100) * yScale(point[1]),
              ])
            ),
            'Z',
          ].join(' ')}
        />
      )}
      {[BladeSide.LeadingEdge, BladeSide.TrailingEdge].includes(bladeSide) && (
        <path
          className="selection-range"
          ref={boxRef}
          d={[
            'M',
            rawSelection[0][0],
            35,
            'L',
            rawSelection[1][0],
            35,
            'L',
            rawSelection[1][0],
            47,
            'L',
            rawSelection[0][0],
            47,
            'Z',
          ].join(' ')}
        />
      )}
      {!hideLabel && (
        <>
          <rect
            transform={`translate(${labelParameters.x}, ${labelParameters.y})`}
            fill={theme.secondaryBackgroundColor}
            opacity={0.6}
            rx={4}
            ry={4}
            {...textBackgroundAttributes}
          />
          <text
            ref={textRef}
            className="selection-range-label"
            transform={`translate(${labelParameters.x}, ${labelParameters.y})`}
            style={{ textAnchor: labelParameters.textAnchor }}
            fill={theme.plot.axisColor}
          >
            <tspan x="0" dy="1em">
              dist: {distance[0].toFixed(1)}m to {distance[1].toFixed(1)}m
            </tspan>

            {chord && (
              <tspan x="0" dy="1em">
                chord: {chord[0].toFixed(0)}% to {chord[1].toFixed(0)}%
              </tspan>
            )}
          </text>
        </>
      )}
    </>
  );
};
